// ============================================================
// TopDeals — "Top Deals by Score" widget for manager/admin dashboard
// Fetches top N leads by deal_score and shows ranked list
// Props: accent {string}, limit {number}
// ============================================================
import React, { useEffect, useState } from "react";
import API from "../utils/api";
import ScoreRing from "./ScoreRing";

const STAGE_LABEL = {
  visited: "Visited", interested: "Interested",
  proposal_sent: "Proposal Sent", mou_shared: "MOU Shared",
  mou_signed: "MOU Signed", payment_requested: "Payment Req.",
  closed_won: "Won", closed_lost: "Lost",
};

const STAGE_COLOR = {
  visited: "#64748b", interested: "#1d4ed8", proposal_sent: "#c2410c",
  mou_shared: "#7c3aed", mou_signed: "#4f46e5",
  payment_requested: "#b45309", closed_won: "#15803d", closed_lost: "#dc2626",
};

const TEMP_ICON = { hot: "🔥", warm: "🌤", cold: "❄️" };

export default function TopDeals({ accent = "#E31E24", limit = 10 }) {
  const [leads, setLeads]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    API.get("/leads/top-scores", { params: { limit } })
      .then(r => { setLeads(r.data); setLoading(false); })
      .catch(e => { setError(e?.response?.data?.error || "Failed to load"); setLoading(false); });
  }, [limit]);

  if (loading) return (
    <div style={{ textAlign: "center", color: "#94a3b8", padding: 32 }}>Loading top deals…</div>
  );

  if (error) return (
    <div style={{ textAlign: "center", color: "#dc2626", padding: 20, fontSize: 13 }}>{error}</div>
  );

  if (leads.length === 0) return (
    <div style={{ textAlign: "center", padding: 32, color: "#94a3b8" }}>
      <div style={{ fontSize: 36, marginBottom: 8 }}>🎯</div>
      <div style={{ fontWeight: 600 }}>No scored leads yet</div>
      <div style={{ fontSize: 12, marginTop: 4 }}>Scores refresh nightly at 2 AM</div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#1e293b" }}>🎯 Top Deals by Score</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>Ranked by AI deal health score · refreshed nightly</div>
        </div>
        <div style={{ background: "#f1f5f9", borderRadius: 8, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: "#64748b" }}>
          Top {leads.length}
        </div>
      </div>

      {/* Grade summary pills */}
      {(() => {
        const counts = { A: 0, B: 0, C: 0, D: 0, F: 0 };
        leads.forEach(l => { if (l.deal_grade) counts[l.deal_grade]++; });
        const GCOL = { A: "#16a34a", B: "#2563eb", C: "#d97706", D: "#ea580c", F: "#dc2626" };
        return (
          <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
            {Object.entries(counts).filter(([, v]) => v > 0).map(([g, v]) => (
              <span key={g} style={{ background: "#f8fafc", border: `1.5px solid ${GCOL[g]}20`, borderRadius: 8, padding: "3px 10px", fontSize: 11, fontWeight: 700, color: GCOL[g] }}>
                Grade {g}: {v} lead{v > 1 ? "s" : ""}
              </span>
            ))}
          </div>
        );
      })()}

      {/* Lead rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {leads.map((l, idx) => (
          <div key={l.id} style={{ background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 14, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12 }}>
            {/* Rank */}
            <div style={{ fontSize: 13, fontWeight: 800, color: idx < 3 ? accent : "#94a3b8", width: 20, textAlign: "center", flexShrink: 0 }}>
              {idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `#${idx + 1}`}
            </div>

            {/* Score ring */}
            <ScoreRing score={l.deal_score} grade={l.deal_grade || "F"} size={44} />

            {/* Details */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {l.institution_name}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
                {/* Stage */}
                <span style={{ fontSize: 10, fontWeight: 700, color: STAGE_COLOR[l.pipeline_stage] || "#64748b",
                  background: `${STAGE_COLOR[l.pipeline_stage] || "#64748b"}15`,
                  borderRadius: 6, padding: "2px 6px" }}>
                  {STAGE_LABEL[l.pipeline_stage] || l.pipeline_stage}
                </span>
                {/* Temp */}
                {l.lead_status && (
                  <span style={{ fontSize: 10 }}>{TEMP_ICON[l.lead_status] || ""} {l.lead_status}</span>
                )}
                {/* Rep name (for manager/admin view) */}
                {l.rep_name && (
                  <span style={{ fontSize: 10, color: "#94a3b8" }}>👤 {l.rep_name}</span>
                )}
              </div>
            </div>

            {/* Flags if any */}
            {l.deal_flags && l.deal_flags.length > 0 && (
              <div style={{ flexShrink: 0 }}>
                <span style={{ background: "#fef3c7", border: "1px solid #fde047", borderRadius: 6, padding: "2px 6px", fontSize: 10, fontWeight: 700, color: "#92400e" }}>
                  {l.deal_flags.length} alert{l.deal_flags.length > 1 ? "s" : ""}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ textAlign: "center", marginTop: 12, fontSize: 11, color: "#cbd5e1" }}>
        Scores based on stage, temperature, follow-ups, recency, deal value
      </div>
    </div>
  );
}
